import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseProps } from 'services/services.types';

import { baseQueryWithReauth } from 'utils/services';

import { setUser } from '../../store/user/userSlice';

import { DocumentItem } from 'models/document.types';

import {
  GenerateDynamicLinkQueueReqProps,
  GenerateDynamicLinkResProps,
  GetGlobalMessagesResProps,
  GetSignableDocumentsReqProps,
  GetSignableDocumentsResProps,
  GetTokenResProps,
  SendMedicationMigrationEnrollReqProps,
  SendMedicationMigrationEnrollResProps,
  SendMigrationDecisionReqProps,
  SignDocumentReqProps,
  SignDocumentResProps,
  UnSignDocumentReqProps,
  UnSignDocumentResProps,
  VerifyIdentityCrosscheckResProps,
  VerifyIdentityReqProps
} from './general.types';

// This is for any not related requests
export const generalApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    generateDynamicLinkQueue: build.mutation<
      GenerateDynamicLinkResProps,
      GenerateDynamicLinkQueueReqProps
    >({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          Authorization: accessToken
        },
        method: 'POST',
        url: '/dynamic-links'
      })
    }),
    getGlobalMessages: build.query<GetGlobalMessagesResProps, void>({
      query: () => ({
        url: '/global-messages'
      })
    }),
    getSignableDocuments: build.query<GetSignableDocumentsResProps, GetSignableDocumentsReqProps>({
      keepUnusedDataFor: 0,
      query: (params) => ({
        headers: {
          ...(params?.accessToken && { Authorization: params.accessToken })
        },
        method: 'GET',
        url: '/signable-documents'
      })
    }),
    getTokenForCancel: build.mutation<GetTokenResProps, void>({
      query: () => ({
        method: 'POST',
        url: '/typeform/cancel-subscription'
      })
    }),
    sendMedicationMigrationEnroll: build.mutation<
      SendMedicationMigrationEnrollResProps,
      SendMedicationMigrationEnrollReqProps
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(
              setUser({
                semaglutideMigration: data.data
              })
            );
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(e);
        }
      },
      query: (body) => ({
        body,
        method: 'POST',
        url: '/medication-migration/enroll'
      })
    }),
    sendMigrationDecision: build.mutation<unknown, SendMigrationDecisionReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/medication-migration/track-action'
      })
    }),
    signDocument: build.mutation<SignDocumentResProps, SignDocumentReqProps>({
      query: ({ accessToken, templateId }) => ({
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        method: 'POST',
        url: `/sign-document/${templateId}`
      })
    }),
    unSignDocument: build.mutation<UnSignDocumentResProps, UnSignDocumentReqProps>({
      query: ({ documentId }) => ({
        method: 'POST',
        url: `/unsign-document/${documentId}`
      })
    }),
    verifyIdentity: build.mutation<
      ResponseProps<NonNullable<DocumentItem['identity']>>,
      VerifyIdentityReqProps
    >({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        method: 'POST',
        url: '/verify-identity'
      })
    }),
    verifyIdentityViaCrossCheck: build.mutation<VerifyIdentityCrosscheckResProps, void>({
      query: () => ({
        method: 'POST',
        url: '/verify-identity/crosscheck'
      })
    })
  }),
  reducerPath: 'generalApi',
  tagTypes: ['General']
});

export const {
  useGenerateDynamicLinkQueueMutation,
  useGetTokenForCancelMutation,
  useGetSignableDocumentsQuery,
  useVerifyIdentityMutation,
  useVerifyIdentityViaCrossCheckMutation,
  useSignDocumentMutation,
  useUnSignDocumentMutation,
  useGetGlobalMessagesQuery,
  useSendMigrationDecisionMutation,
  useSendMedicationMigrationEnrollMutation
} = generalApi;
